'use client'

import { useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import {
  popupStyleToCss,
  overlayStyleToCss,
  type ResolvedPopupStyle,
} from './popup-style'

export type PopupLayout = 'modal' | 'slide_in' | 'bottom_bar' | 'takeover'
export type PopupCorner = 'bottom_right' | 'bottom_left' | 'top_right' | 'top_left'
export type PopupAnimation = 'none' | 'fade' | 'slide' | 'zoom'

/** @deprecated retained as an alias for back-compat; prefer PopupCorner. */
export type SlideInCorner = PopupCorner

export type PopupShellProps = {
  layout: PopupLayout
  slideInCorner?: PopupCorner
  resolvedStyle: ResolvedPopupStyle
  backdropDim?: boolean
  dismissible?: boolean
  animation?: PopupAnimation
  onDismiss: () => void
  reducedMotion?: boolean
  /** ID applied to the headline element so we can wire aria-labelledby. */
  headlineId?: string
  /** When true, render `backgroundMediaUrl` as a full-card backdrop with a
   * subtle scrim so text stays legible. Used by media_position='background'. */
  hasBackgroundMedia?: boolean
  backgroundMediaUrl?: string | null
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Per-layout default animation when the popup says 'fade' / 'slide' / 'zoom' —
// we map the semantic name to a concrete keyframe + duration. Layouts pin to
// the keyframe that reads best (e.g. bottom_bar always slides up regardless
// of choice except 'none', because zoom on a full-width bar looks broken).
function animationClass(
  layout: PopupLayout,
  animation: PopupAnimation,
  reducedMotion: boolean,
): string {
  if (reducedMotion || animation === 'none') return ''
  if (layout === 'bottom_bar') {
    return animation === 'fade'
      ? 'motion-safe:animate-[popup-fade-in_180ms_ease-out]'
      : 'motion-safe:animate-[popup-slide-up_220ms_ease-out]'
  }
  if (layout === 'slide_in') {
    return animation === 'fade'
      ? 'motion-safe:animate-[popup-fade-in_180ms_ease-out]'
      : animation === 'zoom'
        ? 'motion-safe:animate-[popup-zoom-in_220ms_ease-out]'
        : 'motion-safe:animate-[popup-slide-up_220ms_ease-out]'
  }
  // modal + takeover
  if (animation === 'slide') return 'motion-safe:animate-[popup-slide-up_220ms_ease-out]'
  if (animation === 'zoom') return 'motion-safe:animate-[popup-zoom-in_200ms_ease-out]'
  return 'motion-safe:animate-[popup-fade-in_180ms_ease-out]'
}

function slideInPositionClass(corner: PopupCorner): string {
  switch (corner) {
    case 'bottom_left':
      return 'bottom-4 sm:bottom-6 left-4 sm:left-6'
    case 'top_right':
      return 'top-4 sm:top-6 right-4 sm:right-6'
    case 'top_left':
      return 'top-4 sm:top-6 left-4 sm:left-6'
    case 'bottom_right':
    default:
      return 'bottom-4 sm:bottom-6 right-4 sm:right-6'
  }
}

export function PopupShell({
  layout,
  slideInCorner = 'bottom_right',
  resolvedStyle,
  backdropDim = true,
  dismissible = true,
  animation = 'fade',
  onDismiss,
  reducedMotion = false,
  headlineId,
  hasBackgroundMedia = false,
  backgroundMediaUrl = null,
  children,
}: PopupShellProps) {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const generatedId = useId()
  const labelledBy = headlineId ?? `popup-headline-${generatedId}`

  // Esc + focus trap for modal/takeover.
  useEffect(() => {
    if (layout !== 'modal' && layout !== 'takeover') return
    const card = cardRef.current
    if (!card) return

    const focusables = card.querySelectorAll<HTMLElement>(FOCUSABLE)
    const first = focusables[0]
    const previouslyFocused = document.activeElement as HTMLElement | null
    if (first) {
      first.focus()
    } else {
      card.setAttribute('tabindex', '-1')
      card.focus()
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation()
        onDismiss()
        return
      }
      if (e.key !== 'Tab') return
      const list = card!.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (list.length === 0) return
      const firstEl = list[0]
      const lastEl = list[list.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [layout, dismissible, onDismiss])

  const cardStyle = popupStyleToCss(resolvedStyle)
  const overlayStyle = overlayStyleToCss(resolvedStyle)
  const animClass = animationClass(layout, animation, reducedMotion)

  // Background-media decoration applies to any layout that has a card the
  // user can see "behind" (i.e. not bottom_bar). Renders as a CSS background
  // on the card plus a soft scrim for legibility.
  const cardBackgroundLayer =
    hasBackgroundMedia && backgroundMediaUrl ? (
      <>
        <div
          aria-hidden
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            backgroundImage: `url("${backgroundMediaUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 'inherit',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)',
            borderRadius: 'inherit',
          }}
        />
      </>
    ) : null

  const dismissBtn = dismissible ? (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Close"
      className="absolute right-3 top-3 inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-black/10 transition-colors z-10"
      style={{ color: 'currentColor' }}
    >
      <X size={16} />
    </button>
  ) : null

  if (layout === 'modal' || layout === 'takeover') {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        role="presentation"
      >
        {backdropDim && (
          <div
            className="absolute inset-0"
            style={overlayStyle}
            onClick={dismissible ? onDismiss : undefined}
            aria-hidden
          />
        )}
        <div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          data-popup-layout={layout}
          className={
            (layout === 'takeover'
              ? 'relative w-full h-full max-w-[100vw] max-h-[100vh] overflow-auto flex items-center justify-center isolate '
              : 'relative w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-auto isolate ') +
            animClass
          }
          style={cardStyle}
        >
          {cardBackgroundLayer}
          {layout === 'takeover' ? (
            <div className="w-full max-w-2xl mx-auto">{children}</div>
          ) : (
            children
          )}
          {dismissBtn}
        </div>
      </div>
    )
  }

  if (layout === 'slide_in') {
    return (
      <div
        ref={cardRef}
        role="dialog"
        aria-labelledby={labelledBy}
        data-popup-layout="slide_in"
        className={`fixed ${slideInPositionClass(slideInCorner)} z-[9999] max-w-sm isolate ${animClass}`}
        style={{ width: 'calc(100vw - 2rem)', ...cardStyle }}
      >
        {cardBackgroundLayer}
        {children}
        {dismissBtn}
      </div>
    )
  }

  // bottom_bar — no background-media variant; bar is too thin for it.
  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-labelledby={labelledBy}
      data-popup-layout="bottom_bar"
      className={`fixed bottom-0 left-0 right-0 z-[9999] ${animClass}`}
      style={{
        ...cardStyle,
        borderRadius: 0,
        padding: '0.75rem 1rem',
        paddingRight: dismissible ? '2.5rem' : '1rem',
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 'var(--max-width-page, 1280px)' }}>
        {children}
      </div>
      {dismissBtn}
    </div>
  )
}
